import { jwtDecode } from 'jwt-decode';
import { fetchRefreshTokenGraphQL } from './fetchRefreshToken';

export const TOKEN_STORAGE_KEYS = 'auth_token_storage';

export interface ITokenService {
    refreshToken: () => Promise<void>;
    getToken: () => string;
    saveToken: (token: string, refreshToken: string) => void;
    forceRefreshToken: () => Promise<void>;
}

class TokenService implements ITokenService {
    static #instance: TokenService | null = null;
    #refreshInFlight: Promise<void> | null = null;

    private constructor() {
        return;
    }

    static getInstance(): TokenService {
        if (!TokenService.#instance) {
            TokenService.#instance = new TokenService();
        }

        return TokenService.#instance;
    }

    getToken(): string {
        const tokenData = localStorage.getItem(TOKEN_STORAGE_KEYS);

        return tokenData ? JSON.parse(tokenData).token : '';
    }

    saveToken(token: string, refreshToken: string): void {
        localStorage.setItem(
            TOKEN_STORAGE_KEYS,
            JSON.stringify({
                expiresAt: this.#decodeExpiration(token),
                refreshToken,
                token,
            }),
        );
    }

    shouldRefreshToken(): boolean {
        const tokenData = localStorage.getItem(TOKEN_STORAGE_KEYS);

        if (tokenData) {
            const { refreshToken, expiresAt } = JSON.parse(tokenData) ?? {};

            return refreshToken && Date.now() >= expiresAt * 1000;
        }

        return true;
    }

    async refreshToken(): Promise<void> {
        const tokenData = localStorage.getItem(TOKEN_STORAGE_KEYS);
        if (tokenData) {
            const { refreshToken, expiresAt } = JSON.parse(tokenData) ?? {};

            if (refreshToken && Date.now() >= expiresAt * 1000) {
                await this.#getOrStartRefresh(refreshToken);
            }
        }
    }

    async forceRefreshToken(): Promise<void> {
        const tokenData = localStorage.getItem(TOKEN_STORAGE_KEYS);

        if (tokenData) {
            const { refreshToken } = JSON.parse(tokenData) ?? {};
            if (!refreshToken) return;
            await this.#getOrStartRefresh(refreshToken, { force: true });
        }
    }

    async #getOrStartRefresh(refreshToken: string, options?: { force?: boolean }): Promise<void> {
        if (!options?.force && this.#refreshInFlight) return await this.#refreshInFlight;

        if (this.#refreshInFlight) return await this.#refreshInFlight;

        this.#refreshInFlight = (async () => {
            const response = await fetchRefreshTokenGraphQL(refreshToken);
            const { token, refresh_token } = response;

            const expiresAt = this.#decodeExpiration(token);

            localStorage.setItem(
                TOKEN_STORAGE_KEYS,
                JSON.stringify({
                    token,
                    refreshToken: refresh_token,
                    expiresAt,
                }),
            );
        })();

        try {
            await this.#refreshInFlight;
        } finally {
            this.#refreshInFlight = null;
        }
    }

    #decodeExpiration(token: string): number {
        try {
            const decoded = jwtDecode(token);

            if (decoded && typeof decoded === 'object' && 'exp' in decoded) {
                return Number(decoded.exp);
            }
        } catch (error) {
            console.error('JWT Token is invalid or expired');
        }

        return 0;
    }
}

export default TokenService;
